'use client';

import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/providers/i18n-provider';
import { useTurnstile } from '@/hooks/use-turnstile';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export function ContactContent() {
  const { t } = useTranslation();
  const { languageCode } = useLanguage();
  const searchParams = useSearchParams();
  const companyFromUrl = searchParams.get('company');
  const subjectFromUrl = searchParams.get('subject') ?? '';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: subjectFromUrl,
    message: '',
  });

  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';
  const { token: turnstileToken, reset: resetTurnstile, containerRef } = useTurnstile(siteKey);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!turnstileToken) {
      setError(t('pages.contact.form.errors.turnstile'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          turnstileToken,
          language: languageCode === 'de' ? 'de' : 'en',
          ...(companyFromUrl ? { company: companyFromUrl } : {}),
        }),
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setError(t('pages.contact.form.errors.failedDesc'));
      }
    } catch {
      setError(t('pages.contact.form.errors.failedDesc'));
    } finally {
      setIsSubmitting(false);
      resetTurnstile();
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
      <div className="col-span-2">
        <Card>
          <CardHeader>
            <CardTitle as="h1">{t('pages.contact.form.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {success && (
              <Alert variant="success" className="mb-4">
                <AlertIcon>
                  <CheckCircle2 className="w-4 h-4" />
                </AlertIcon>
                <div>
                  <AlertTitle>
                    {t('pages.contact.form.success.title')}
                  </AlertTitle>
                  <AlertDescription>
                    {t('pages.contact.form.success.description')}
                  </AlertDescription>
                </div>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertIcon>
                  <AlertCircle className="w-4 h-4" />
                </AlertIcon>
                <div>
                  <AlertTitle>
                    {t('pages.contact.form.errors.failed')}
                  </AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </div>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('pages.contact.form.name')}</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('pages.contact.form.namePlaceholder')}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('pages.contact.form.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('pages.contact.form.emailPlaceholder')}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">
                  {t('pages.contact.form.subject')}
                </Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder={t('pages.contact.form.subjectPlaceholder')}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">
                  {t('pages.contact.form.message')}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('pages.contact.form.messagePlaceholder')}
                  rows={6}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div ref={containerRef} />

              <Button type="submit" disabled={isSubmitting || !turnstileToken} className="w-full">
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isSubmitting
                  ? t('pages.contact.form.sending')
                  : t('pages.contact.form.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <div className="col-span-1">
        <Card>
          <CardHeader>
            <CardTitle>{t('pages.contact.info.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <h4 className="font-medium text-sm mb-2">
                {t('pages.contact.info.responseTime')}
              </h4>
              <p className="text-sm text-muted-foreground">
                {t('pages.contact.info.responseTimeDesc')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
